// The page stays put while an overlay owns the screen — 4 Sep 2026, from Natanel's phone.
//
// A swipe inside the find drawer scrolled the page underneath it, and the ⌘K palette, the menu
// drawer and the phone's full-height dialogs were no different: nothing locked the document
// while something sat over it. `overflow: hidden` on the body is all a desktop browser needs;
// iOS Safari ignores it on a page taller than the screen. So the body is PINNED — `position:
// fixed` at its current scroll offset, which is the one approach Safari respects — and put back
// where it was on release, offset included, so the recruiter sees no jump either way.
//
// Ref-counted, because overlays stack: the menu can open over the drawer, and the first one to
// close must not unlock what the second still holds. The phone check asserts the effect (nothing
// left to scroll under an open overlay — harness/screens.ts, `OVERLAY_LOCKS_THE_PAGE`), never
// this mechanism.
import { useEffect } from 'react';

let holders = 0;
let restore: (() => void) | null = null;

function lock(): void {
  holders += 1;
  if (holders > 1) return;
  const { body, documentElement } = document;
  const y = window.scrollY;
  const was = {
    position: body.style.position,
    top: body.style.top,
    left: body.style.left,
    right: body.style.right,
    width: body.style.width,
    overflow: body.style.overflow,
  };
  body.style.position = 'fixed';
  body.style.top = `-${y}px`;
  body.style.left = '0';
  body.style.right = '0';
  body.style.width = '100%';
  body.style.overflow = 'hidden';
  documentElement.dataset['scrollLocked'] = '';
  restore = () => {
    Object.assign(body.style, was);
    delete documentElement.dataset['scrollLocked'];
    window.scrollTo(0, y);
  };
}

function release(): void {
  holders = Math.max(0, holders - 1);
  if (holders === 0 && restore) {
    restore();
    restore = null;
  }
}

/** Holds the page still while `active`; releases on unmount or when it turns false. */
export function useScrollLock(active: boolean): void {
  useEffect(() => {
    if (!active) return;
    lock();
    return release;
  }, [active]);
}
